import styled from '@emotion/styled';
import { useMemo } from 'react';
import { Season } from './apiService';
import { __t } from './i18n';
import Select from './Select';
import { SelectInputProps } from './SelectInput';
import { Flex, Text } from './styles';
import { getEpisodeIdentifier } from './utils';
import { globalPadding } from './Layout/Layout';

type SeasonSelectorProps = {
  seasons: Season[];
  active: number;
  onChange: (season: number) => void;
};

const SeasonSelector = ({ seasons, active, onChange }: SeasonSelectorProps) => {
  const options = useMemo(() => seasons.map(({ season = 0 }, idx) => ({
    value: idx,
    label: getEpisodeIdentifier({ season }),
  })), [seasons]);

  const handleChange: SelectInputProps['onChange'] = (e) => {
    onChange(Number(e.target.value));
  };

  return (
    <Container mb={{ xs: 2, md: 1 }} align="center" justify={{ xs: 'space-between', md: 'flex-start' }}>
      <Text variant="h2" weight="medium">
        {__t('content.type.episodes')}
      </Text>
      {seasons.length > 1 && (
        <Select
          options={options}
          value={active}
          onChange={handleChange}
          mr={2}
          mb={1}
          inputSize="small"
          renderValue={<RenderValue value={active + 1} length={seasons.length} />}
        />
      )}
    </Container>
  );
};

const Container = styled(Flex)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    backgroundImage: `linear-gradient(to bottom, ${theme.palette.background[700][0]}, rgba(0,0,0,0))`,
    marginLeft: globalPadding.sm && `-${theme.spacings[globalPadding.sm]}rem`,
    marginRight: globalPadding.sm && `-${theme.spacings[globalPadding.sm]}rem`,
    padding: theme.spacing(1, 4),
  },
  [theme.breakpoints.down('sm')]: {
    marginLeft: globalPadding.xs && `-${theme.spacings[globalPadding.xs]}rem`,
    marginRight: globalPadding.xs && `-${theme.spacings[globalPadding.xs]}rem`,
    padding: theme.spacing(1, 2),
  },
}));

const RenderValue = ({ value, length }: { value: number; length: number }) => (
  <div>
    <Text variant="body1" as="span" weight="medium">
      {getEpisodeIdentifier({ season: value })}
    </Text>
    <Text variant="body2" as="span" color="weak" mr={1}>
      از {length}
    </Text>
  </div>
);

export default SeasonSelector;
